import { useState, useEffect, useCallback } from 'react';
import { nativeSigningApi } from '../services/nativeSigningApi';

export interface SigningSessionData {
  id: string;
  envelope_id: string;
  document: {
    id: string;
    title: string;
    pdf_url: string;
    page_count: number;
  };
  signer: {
    id: string;
    email: string;
    order: number;
  };
  sender: {
    name: string;
    email: string;
  };
  fields: Array<{
    id: string;
    type: string;
    signerId: string;
    signerEmail: string;
    page: number;
    rect: {
      x1: number;
      y1: number;
      x2: number;
      y2: number;
    };
    required: boolean;
    completed: boolean;
    label: string;
    placeholder: string;
  }>;
  status: string;
  can_sign: boolean;
  is_current_signer: boolean;
  current_signer?: string;
}

export const useSigningSession = (token: string) => {
  const [sessionData, setSessionData] = useState<SigningSessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch session data
  const fetchSession = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      const response = await nativeSigningApi.getSession(token);

      if (response.success) {
        setSessionData(response.session);
      } else {
        setError(response.error || 'Failed to load signing session');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An error occurred';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Start signing session
  const startSession = useCallback(async () => {
    if (!token) {
      return { success: false, error: 'No token provided' };
    }

    try {
      const response = await nativeSigningApi.startSession(token);

      if (response.success && sessionData) {
        // Update session status locally without refetching
        setSessionData({
          ...sessionData,
          status: 'in_progress',
        });
      }

      return response;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start session';
      return {
        success: false,
        error: message,
      };
    }
  }, [token, sessionData]);

  // Submit signature
  const submitSignature = useCallback(
    async (
      signatureData: string,
      initialsData: string,
      consentGiven: boolean,
      fieldValues?: Record<
        string,
        {
          value: string;
          type: 'text' | 'date';
          coordinates: { x: number; y: number; width: number; height: number; page: number };
          signerId: string;
          signerEmail: string;
        }
      >
    ) => {
      if (!token) {
        return { success: false, error: 'No token provided' };
      }

      try {
        // Updated for PDF.js - no XFDF data needed
        const response = await nativeSigningApi.submitSignature(token, {
          signature_data: signatureData,
          initials_data: initialsData,
          consent_given: consentGiven,
          field_values: fieldValues || {},
        });

        if (response.success) {
          // Update session status
          setSessionData((prev) =>
            prev
              ? {
                  ...prev,
                  status: 'completed',
                }
              : null
          );
        }

        return response;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to submit signature';
        return {
          success: false,
          error: message,
        };
      }
    },
    [token]
  );

  // Download signed document
  const downloadDocument = useCallback(async () => {
    if (!token) {
      return { success: false, error: 'No token provided' };
    }

    try {
      // Open download URL in new window
      window.open(`/api/v1/native-signing/${token}/download`, '_blank');
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to download document';
      return {
        success: false,
        error: message,
      };
    }
  }, [token]);

  // Initial fetch
  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Refresh session periodically if waiting
  useEffect(() => {
    if (sessionData?.status === 'waiting' && !sessionData.is_current_signer) {
      const interval = setInterval(fetchSession, 10000); // Check every 10 seconds
      return () => clearInterval(interval);
    }
  }, [sessionData, fetchSession]);

  return {
    sessionData,
    loading,
    error,
    startSession,
    submitSignature,
    downloadDocument,
    refreshSession: fetchSession,
  };
};
